import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight, MapPin, Layers, Calendar, BarChart3, Palette, Users,
    ShieldCheck, Sparkles, Zap, Hash, DollarSign, Mail, Phone,
    PlayCircle, Building2, CheckCircle2, FileText, Briefcase, Presentation,
} from "lucide-react";
import { Button } from "../components/ui/button";
import InvestorDemoButton from "../components/InvestorDemoButton";

/**
 * Public-facing marketing page for FMOs / agencies / agents.
 * URL: /demo
 *
 * This page is intentionally separated from the consumer experience.
 * Real annuity-review prospects never see this — it is for partnership inquiries only.
 */
export default function DemoForFMOs() {
    return (
        <div className="min-h-screen bg-[#0b1220] text-white" data-testid="fmo-demo-page">
            {/* Hero */}
            <section className="relative overflow-hidden border-b border-white/5">
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute -top-32 -right-40 w-[640px] h-[640px] rounded-full bg-amber-400/10 blur-3xl" />
                    <div className="absolute -bottom-40 -left-20 w-[520px] h-[520px] rounded-full bg-emerald-400/10 blur-3xl" />
                </div>
                <div className="relative max-w-7xl mx-auto px-6 sm:px-8 py-16 md:py-24">
                    <div className="flex items-center gap-3 mb-6">
                        <Link to="/" className="inline-flex items-center gap-2 text-blue-200 hover:text-white text-sm" data-testid="demo-back-home">
                            <ShieldCheck className="w-4 h-4" /> Back to consumer site
                        </Link>
                    </div>
                    <div className="inline-flex items-center gap-2 uppercase tracking-[0.3em] text-xs font-bold text-amber-300 mb-6 bg-amber-400/10 border border-amber-400/30 rounded-full px-4 py-1.5">
                        <Building2 className="w-3.5 h-3.5" /> For FMOs · Agencies · Agents
                    </div>
                    <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-5xl">
                        A white-label annuity review platform
                        <span className="block text-amber-300 mt-2">your producers actually use.</span>
                    </h1>
                    <p className="mt-8 text-lg sm:text-xl text-white/75 max-w-3xl leading-relaxed">
                        Capture annuity-owner leads, route them by ZIP to the right licensed agent, track every conversion, and brand the experience as your FMO — all from one platform you can deploy in a week.
                    </p>
                    <div className="mt-10 flex flex-col sm:flex-row gap-4">
                        <InvestorDemoButton />
                        <a href="/pitch" target="_blank" rel="noreferrer" data-testid="demo-hero-pitch-cta">
                            <Button variant="outline" className="bg-white/5 backdrop-blur border-white/40 text-white hover:bg-white/15 hover:text-white px-8 py-7 text-base font-medium rounded-md w-full sm:w-auto whitespace-nowrap">
                                <Presentation className="w-4 h-4 mr-2" /> View Pitch Deck
                            </Button>
                        </a>
                        <a href="#contact" data-testid="demo-hero-contact-cta">
                            <Button variant="outline" className="bg-white/5 backdrop-blur border-white/40 text-white hover:bg-white/15 hover:text-white px-8 py-7 text-base font-medium rounded-md w-full sm:w-auto whitespace-nowrap">
                                Partnership inquiries <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </a>
                    </div>
                    <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-4">
                        <StatPill big="Live" label="Single-agent nationwide deployment" />
                        <StatPill big="< 1 week" label="To onboard a new producer" />
                        <StatPill big="Zero" label="Dev cost to the FMO" />
                        <StatPill big="ZIP-based" label="Automatic lead routing" />
                    </div>
                </div>
            </section>

            {/* THE PRODUCT */}
            <Section title="What it actually is" eyebrow="The product">
                <div className="grid lg:grid-cols-3 gap-5">
                    <FeatureCard
                        icon={<FileText className="w-5 h-5" />}
                        title="6-step intake wizard"
                        desc="Annuity owner answers contact, contract details, features, needs discovery, statement upload, and review. Each step takes ~30 seconds."
                    />
                    <FeatureCard
                        icon={<BarChart3 className="w-5 h-5" />}
                        title="Annuity scorecard"
                        desc="Instant snapshot of fees, flexibility, and income strength based on the contract type. Sets expectations before the call."
                    />
                    <FeatureCard
                        icon={<Calendar className="w-5 h-5" />}
                        title="Calendly booking flow"
                        desc="Lead lands on a booking page after the scorecard. Calls show up in the agent's calendar automatically — no back-and-forth."
                    />
                    <FeatureCard
                        icon={<MapPin className="w-5 h-5" />}
                        title="ZIP-based lead routing"
                        desc="Every submission is matched to the right licensed agent by ZIP code, then state, then round-robin balanced by current lead count."
                    />
                    <FeatureCard
                        icon={<Users className="w-5 h-5" />}
                        title="Agent dashboards"
                        desc="Each agent sees only their assigned leads, statuses, notes, statement downloads, and a public profile they self-edit."
                    />
                    <FeatureCard
                        icon={<DollarSign className="w-5 h-5" />}
                        title="Commission + override tracking"
                        desc="FMO admin records lead fee, premium, commission, and override % per lead. Override amount auto-calculates."
                    />
                </div>
            </Section>

            {/* HOW LEAD ROUTING WORKS */}
            <Section title="How lead routing works" eyebrow="Routing">
                <div className="grid md:grid-cols-2 gap-10 items-start">
                    <div>
                        <p className="text-white/80 text-lg leading-relaxed">
                            Every submission runs through a deterministic 4-step routing pipeline that takes under 50 milliseconds — no human in the middle.
                        </p>
                        <ol className="mt-8 space-y-4">
                            <RoutingStep n="1" title="Agent landing page (if any)" desc="If the lead came from /agent/<slug>, that slug wins. Lets agents own their lead-gen funnel." />
                            <RoutingStep n="2" title="ZIP code territory" desc="Lead's ZIP is matched against each agent's territory list. First match wins (or round-robin if multiple)." />
                            <RoutingStep n="3" title="License state" desc="If no ZIP match, the lead's state is checked against each agent's licensed states. Multiple agents → round-robin by current lead count." />
                            <RoutingStep n="4" title="Unassigned pool" desc="No match? Lead lands in the unassigned pool with full FMO admin notification. Admin can assign manually." />
                        </ol>
                    </div>
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                        <div className="text-xs uppercase tracking-[0.2em] text-amber-300 font-semibold mb-3">Live example</div>
                        <div className="space-y-2.5 text-sm">
                            <RouteLine icon={Hash} label="ZIP" value="74133 (Tulsa OK)" />
                            <RouteLine icon={MapPin} label="Coverage" value="All 50 States" />
                            <RouteLine icon={ArrowRight} label="Match found" value="Mark Stevens · OK / KS / TX territory" tone="emerald" />
                            <RouteLine icon={Mail} label="Notify" value="Agent + FMO admin (Resend)" />
                            <RouteLine icon={Calendar} label="Booking link" value="Mark's personal Calendly" />
                            <RouteLine icon={CheckCircle2} label="SLA" value="< 50ms end-to-end" tone="emerald" />
                        </div>
                    </div>
                </div>
            </Section>

            {/* SCHEDULING */}
            <Section title="Built-in scheduling" eyebrow="Booking flow">
                <div className="grid lg:grid-cols-2 gap-10 items-center">
                    <div>
                        <p className="text-white/80 text-lg leading-relaxed">
                            Annuity owners book directly into the assigned agent's calendar — no shared inboxes, no playing tag, no leads cooling off overnight.
                        </p>
                        <ul className="mt-8 space-y-3 text-white/80">
                            <Bullet>Each agent provides their own Calendly link in self-service profile</Bullet>
                            <Bullet>Lead sees the assigned agent's photo, bio, license states, and direct calendar</Bullet>
                            <Bullet>Auto-confirmation email goes to the lead, the agent, and the FMO admin</Bullet>
                            <Bullet>When the lead clicks the booking link from email, status flips New → Contacted automatically</Bullet>
                            <Bullet>Engagement tracked: email opens, link clicks, booking completed — visible in admin UI</Bullet>
                        </ul>
                    </div>
                    <div className="bg-gradient-to-br from-amber-400/20 to-emerald-400/10 border border-white/15 rounded-2xl p-8">
                        <div className="text-xs uppercase tracking-[0.2em] text-amber-300 font-semibold mb-4">Lead journey · day one</div>
                        <div className="space-y-3">
                            <JourneyStep time="10:14" event="Lead submits review wizard" tone="sky" />
                            <JourneyStep time="10:14" event="Routed → Mark Stevens (Tulsa ZIP)" tone="amber" />
                            <JourneyStep time="10:14" event="Confirmation email sent (Resend)" tone="amber" />
                            <JourneyStep time="10:18" event="Lead opens email · status: New" tone="emerald" />
                            <JourneyStep time="10:19" event="Clicks calendar · auto-promotes to Contacted" tone="emerald" />
                            <JourneyStep time="10:22" event="Books 30-min call Thursday 2pm" tone="emerald" />
                        </div>
                    </div>
                </div>
            </Section>

            {/* REPORTING */}
            <Section title="Reporting that actually pays for itself" eyebrow="Reporting">
                <div className="grid lg:grid-cols-3 gap-5">
                    <FeatureCard
                        icon={<BarChart3 className="w-5 h-5" />}
                        title="Conversions by agent"
                        desc="Total leads · sold · conversion rate · commission · FMO override — all per agent, all live."
                    />
                    <FeatureCard
                        icon={<DollarSign className="w-5 h-5" />}
                        title="Commission tracker"
                        desc="Per-lead: lead fee, sale status, estimated premium, commission generated, override %, override amount (auto-calculated)."
                    />
                    <FeatureCard
                        icon={<Sparkles className="w-5 h-5" />}
                        title="Hot leads"
                        desc="System flags leads that opened email + clicked the booking link but haven't booked yet — biggest near-term opportunity."
                    />
                    <FeatureCard
                        icon={<FileText className="w-5 h-5" />}
                        title="CSV export"
                        desc="Full pipeline export with engagement metrics, status, agent assignment, and timestamps. Drop into Excel / your BI tool."
                    />
                    <FeatureCard
                        icon={<Mail className="w-5 h-5" />}
                        title="Email engagement"
                        desc="Delivered · opened · clicked tracked via Resend webhooks. Shows up as colored chips next to every lead in the dashboard."
                    />
                    <FeatureCard
                        icon={<ShieldCheck className="w-5 h-5" />}
                        title="Audit-ready"
                        desc="Every status change, assignment, and note is timestamped and attributed. Suitability documentation lives with the lead."
                    />
                </div>
            </Section>

            {/* WHITE LABEL */}
            <Section title="Your brand. Our rails." eyebrow="White label">
                <div className="grid md:grid-cols-2 gap-10 items-center">
                    <div>
                        <p className="text-white/80 text-lg leading-relaxed">
                            Drop in your logo, organization name, primary color, tagline, hero copy, and disclosure text from a single admin screen. Everything live-updates across the consumer site, emails, and PDFs.
                        </p>
                        <ul className="mt-8 space-y-3 text-white/80">
                            <Bullet><span className="font-semibold text-white">Live theme editor</span> — change the brand color and every page updates instantly</Bullet>
                            <Bullet><span className="font-semibold text-white">Logo upload</span> from the admin UI · stored in object storage · served via CDN</Bullet>
                            <Bullet><span className="font-semibold text-white">Owner profile section</span> on the homepage with photo, story, license states</Bullet>
                            <Bullet><span className="font-semibold text-white">Per-agent landing pages</span> — each agent gets <code className="text-amber-300 bg-amber-400/10 px-1.5 py-0.5 rounded text-xs">/agent/their-slug</code> with their own bio, photo, Calendly</Bullet>
                            <Bullet><span className="font-semibold text-white">Multi-tenant ready</span> — same engine, different brand per region or sub-network</Bullet>
                        </ul>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        {[
                            { name: "Your FMO", c1: "#0b1220", c2: "#f59e0b" },
                            { name: "Region B", c1: "#1e3a8a", c2: "#22d3ee" },
                            { name: "Region C", c1: "#064e3b", c2: "#84cc16" },
                            { name: "Producer A", c1: "#7c2d12", c2: "#fbbf24" },
                            { name: "Producer B", c1: "#312e81", c2: "#a78bfa" },
                            { name: "Producer C", c1: "#831843", c2: "#fb7185" },
                        ].map((b) => (
                            <div key={b.name} className="rounded-xl overflow-hidden border border-white/10">
                                <div className="h-20 flex items-end p-3" style={{ background: `linear-gradient(135deg, ${b.c1}, ${b.c2})` }}>
                                    <div className="text-[11px] font-serif text-white drop-shadow truncate">{b.name}</div>
                                </div>
                                <div className="bg-white/5 px-3 py-2 text-[9px] text-white/60 uppercase tracking-wider">Same engine · own brand</div>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            {/* PLATFORM SCALE NOTE — no public roadmap */}
            <Section title="Built to scale with you" eyebrow="Architecture">
                <div className="grid lg:grid-cols-2 gap-10 items-center">
                    <div>
                        <p className="text-white/80 text-lg leading-relaxed">
                            The platform is engineered for an exclusive partnership. Same engine can power
                            a single market or hundreds of producers across multiple states — without rebuilding anything.
                        </p>
                        <ul className="mt-6 space-y-3 text-white/80">
                            <Bullet>ZIP-priority routing with state fallback and round-robin balancing</Bullet>
                            <Bullet>Per-agent landing pages, profiles, and Calendly integration</Bullet>
                            <Bullet>White-label theming · own brand on a proven engine</Bullet>
                            <Bullet>Email + SMS engagement tracking (Resend + webhooks live)</Bullet>
                            <Bullet>Conversion and override reporting per agent and per region</Bullet>
                        </ul>
                    </div>
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-sm text-white/70">
                        <Zap className="w-4 h-4 text-amber-300 inline mr-2" />
                        Built on FastAPI + MongoDB + React. Horizontally scalable. CDN-backed. The platform you'll never outgrow.
                    </div>
                </div>
            </Section>

            {/* CONTACT */}
            <section id="contact" className="border-t border-white/10 bg-gradient-to-b from-transparent to-black/30">
                <div className="max-w-5xl mx-auto px-6 sm:px-8 py-20">
                    <div className="text-center">
                        <div className="inline-flex items-center gap-2 uppercase tracking-[0.3em] text-xs font-bold text-amber-300 mb-6 bg-amber-400/10 border border-amber-400/30 rounded-full px-4 py-1.5">
                            <Briefcase className="w-3.5 h-3.5" /> Partnership inquiries
                        </div>
                        <h2 className="font-serif text-4xl sm:text-5xl leading-tight">Let's talk about deploying this exclusively for your FMO.</h2>
                        <p className="mt-6 text-white/70 text-lg max-w-2xl mx-auto">
                            The platform is built, live, and ready to white-label under your brand. Zero dev cost on your side — just your colors, logo, agents, and carriers.
                        </p>
                    </div>
                    <ContactForm />
                </div>
            </section>

            {/* FOOTER NOTE */}
            <div className="border-t border-white/10 py-8 text-center text-xs text-white/40">
                <p>FMO/agency-only marketing page. Not visible to consumer annuity-review prospects.</p>
                <p className="mt-1.5">
                    Developed and managed by <span className="text-white/70">Strategic Media Holdings, LLC</span> · {" "}
                    <Link to="/" className="hover:text-amber-300">yourannuityreview.com</Link>
                </p>
            </div>
        </div>
    );
}

// ---------- Primitives ----------

function Section({ eyebrow, title, children }) {
    return (
        <section className="max-w-7xl mx-auto px-6 sm:px-8 py-20 border-b border-white/5">
            <div className="text-xs uppercase tracking-[0.3em] text-amber-300 font-semibold mb-3">{eyebrow}</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight mb-12 max-w-4xl">{title}</h2>
            {children}
        </section>
    );
}

function StatPill({ big, label }) {
    return (
        <div className="bg-white/5 border border-white/10 rounded-xl p-4">
            <div className="font-serif text-2xl text-amber-300">{big}</div>
            <div className="mt-1 text-xs text-white/60 leading-snug">{label}</div>
        </div>
    );
}

function FeatureCard({ icon, title, desc }) {
    return (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-white/20 transition-all">
            <div className="w-10 h-10 rounded-md bg-amber-400/15 text-amber-300 flex items-center justify-center mb-4">{icon}</div>
            <div className="font-serif text-xl text-white">{title}</div>
            <div className="mt-2 text-sm text-white/70 leading-relaxed">{desc}</div>
        </div>
    );
}

function Bullet({ children }) {
    return (
        <li className="flex items-start gap-3">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-1 shrink-0" />
            <span>{children}</span>
        </li>
    );
}

function RoutingStep({ n, title, desc }) {
    return (
        <li className="flex items-start gap-4">
            <div className="w-9 h-9 rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-300 font-serif text-lg flex items-center justify-center shrink-0">{n}</div>
            <div>
                <div className="font-semibold text-white">{title}</div>
                <div className="text-sm text-white/65 mt-0.5">{desc}</div>
            </div>
        </li>
    );
}

function RouteLine({ icon: Icon, label, value, tone }) {
    const valueClass = tone === "emerald" ? "text-emerald-300" : "text-white";
    return (
        <div className="flex items-center gap-3 py-1.5">
            <div className="w-7 h-7 rounded-md bg-white/10 text-white/70 flex items-center justify-center shrink-0"><Icon className="w-3.5 h-3.5" /></div>
            <div className="text-xs uppercase tracking-wider text-white/40 w-20">{label}</div>
            <div className={`${valueClass} font-medium`}>{value}</div>
        </div>
    );
}

function JourneyStep({ time, event, tone }) {
    const toneClass = {
        sky: "bg-sky-400/15 border-sky-400/40 text-sky-200",
        amber: "bg-amber-400/15 border-amber-400/40 text-amber-200",
        emerald: "bg-emerald-400/15 border-emerald-400/40 text-emerald-200",
    }[tone] || "bg-white/10 border-white/20 text-white";
    return (
        <div className={`flex items-center gap-3 ${toneClass} border rounded-lg px-3 py-2 text-sm`}>
            <div className="text-xs font-mono opacity-70 w-10">{time}</div>
            <div>{event}</div>
        </div>
    );
}

function ScaleCard({ eyebrow, title, bullets, tone }) {
    return (
        <div className={`bg-gradient-to-br ${tone} rounded-2xl p-6 text-white border border-white/10`}>
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold opacity-80 mb-3">{eyebrow}</div>
            <div className="font-serif text-xl mb-4">{title}</div>
            <ul className="space-y-2 text-sm text-white/85">
                {bullets.map((b) => <li key={b} className="flex items-start gap-2"><CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-white/70" /><span>{b}</span></li>)}
            </ul>
        </div>
    );
}

function ContactForm() {
    const [submitted, setSubmitted] = useState(false);
    const mailto = "mailto:reviews@yourannuityreview.com?subject=FMO%20Partnership%20Inquiry";

    return (
        <div className="mt-12 grid md:grid-cols-2 gap-6">
            <a
                href={mailto}
                onClick={() => setSubmitted(true)}
                className="group bg-amber-400 hover:bg-amber-300 text-slate-900 rounded-2xl p-8 text-center transition-all hover:-translate-y-1 shadow-2xl shadow-amber-500/20"
                data-testid="demo-contact-email"
            >
                <Mail className="w-6 h-6 mx-auto mb-3" />
                <div className="font-serif text-2xl mb-1">Email us</div>
                <div className="text-sm font-medium">reviews@yourannuityreview.com</div>
                <div className="mt-3 inline-flex items-center text-xs font-bold uppercase tracking-wider group-hover:gap-2 gap-1.5 transition-all">
                    {submitted ? "Opening your mail app…" : "Click to compose"} <ArrowRight className="w-3.5 h-3.5" />
                </div>
            </a>
            <Link
                to="/admin/login"
                className="group bg-white/5 hover:bg-white/10 border-2 border-white/15 hover:border-white/25 rounded-2xl p-8 text-center transition-all hover:-translate-y-1"
                data-testid="demo-contact-login"
            >
                <PlayCircle className="w-6 h-6 mx-auto mb-3 text-amber-300" />
                <div className="font-serif text-2xl mb-1">Already a partner?</div>
                <div className="text-sm text-white/70">Sign in to your admin or agent dashboard</div>
                <div className="mt-3 inline-flex items-center text-xs font-bold uppercase tracking-wider text-amber-300 group-hover:gap-2 gap-1.5 transition-all">
                    Go to login <ArrowRight className="w-3.5 h-3.5" />
                </div>
            </Link>
        </div>
    );
}
